import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { fetchGrades } from '../mishtameretPortalApi';
import { formatHebrewDate } from '../../../utils/hebrewDate';

function formatDate(iso) {
  return formatHebrewDate(iso);
}

const GradesScreen = () => {
  const { theme, basePath = '/mishtameret' } = useOutletContext();
  const navigate = useNavigate();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchGrades();
        if (!cancelled) setGrades(data.grades || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת ציונים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  return (
    <Box>
      {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}

      {!grades.length ? (
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.radius}px`,
            border: `1px dashed ${theme.border}`,
            color: theme.textMuted,
            textAlign: 'center',
          }}
        >
          עדיין אין ציונים להצגה
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {grades.map((g) => (
            <Box
              key={g.id}
              sx={{
                p: 2,
                borderRadius: `${theme.radius}px`,
                border: `1px solid ${theme.borderSoft}`,
                bgcolor: theme.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{g.examTitle}</Typography>
                <Typography sx={{ color: theme.textMuted, fontSize: '0.9rem', mt: 0.35 }}>
                  {[g.frameLabel, formatDate(g.finishedAt)].filter(Boolean).join(' · ')}
                </Typography>
                {g.totalQuestions > 0 && (
                  <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
                    {g.correctCount}/{g.totalQuestions} נכונות
                  </Typography>
                )}
                {g.canReview ? (
                  <Button
                    onClick={() => navigate(`${basePath}/exams/take/${g.id}`)}
                    sx={{
                      mt: 1,
                      minHeight: 40,
                      px: 2,
                      borderRadius: 999,
                      border: `1px solid ${theme.border}`,
                      color: theme.text,
                      fontWeight: 700,
                      '&:hover': { bgcolor: theme.surfaceHover },
                    }}
                  >
                    צפייה במבחן
                  </Button>
                ) : (
                  <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                    מבחן מהקו הטלפוני — נשמר הציון בלבד
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                <Typography
                  sx={{
                    fontFamily: theme.fontDisplay,
                    fontWeight: 700,
                    fontSize: '1.75rem',
                    color: theme.accent,
                    lineHeight: 1,
                  }}
                >
                  {g.score}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GradesScreen;
